import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from '../services/local-storage.service';
import { TokenRefreshService } from '../services/token-refresh-service';
import { LoginApiResponse, TokenResponse } from 'src/app/api/identity/models';
import { RedirectService } from '../services/redirect-service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<LoginApiResponse | null>(
    null
  );

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private tokenRefreshService: TokenRefreshService,
    private localStorageService: LocalStorageService,
    private redirectService: RedirectService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.shouldBypassTokenInterceptor(request)) {
      return next.handle(request);
    }

    const authToken = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const isRefreshTokenRequest = request.url.includes('/refresh-token');

    const authRequest = this.addToken(
      request,
      authToken,
      refreshToken,
      isRefreshTokenRequest
    );

    return next.handle(authRequest).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          if (
            error.status === 401 &&
            !isRefreshTokenRequest &&
            !this.isPasswordLoginRequest(request)
          ) {
            if (!authToken) {
              this.toastr.error(error.error.message);
            }
            return this.handle401Error(request, next);
          }
        }
        return throwError(error);
      })
    );
  }

  /** Login must not attach stale tokens or trigger refresh — wrong password returns 401. */
  private shouldBypassTokenInterceptor(request: HttpRequest<unknown>): boolean {
    return this.isPasswordLoginRequest(request);
  }

  private isPasswordLoginRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes('/auth/login');
  }

  private addToken(
    request: HttpRequest<unknown>,
    authToken: string | null,
    refreshToken: string | null,
    isRefreshTokenRequest: boolean
  ): HttpRequest<unknown> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${
        isRefreshTokenRequest ? refreshToken : authToken
      }`,
    };

    return request.clone({
      setHeaders: headers,
    });
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const refreshToken = localStorage.getItem('refreshToken');
    const currentAccessToken = localStorage.getItem('token');

    if (!refreshToken || !currentAccessToken) {
      this.redirectService.setRedirectUrl(this.router.url);
      this.localStorageService.clear();
      this.router.navigate(['/auth']);
      return throwError(
        () => new Error('No refresh or access token available')
      );
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenRefreshService.refreshToken().pipe(
        catchError(error => {
          this.isRefreshing = false;
          // Notify any waiting requests that refresh failed
          this.refreshTokenSubject.error(error);
          // Re-instantiate the subject for future sessions (though we redirect)
          this.refreshTokenSubject =
            new BehaviorSubject<LoginApiResponse | null>(null);

          // Display backend error message on refresh token failure
          const backendErrorMsg =
            error instanceof HttpErrorResponse
              ? this.getApiErrorMessage(error)
              : null;
          this.toastr.error(
            backendErrorMsg || 'Session expired. Please log in again.'
          );

          if (error.status === 401 || error.status === 403 || !error.status) {
            this.redirectService.setRedirectUrl(this.router.url);
            this.localStorageService.clear();
            this.router.navigate(['/auth']);
          }
          return throwError(() => error);
        }),
        switchMap((newTokenResponse: LoginApiResponse) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newTokenResponse);
          return next.handle(this.addNewToken(request, newTokenResponse)).pipe(
            catchError((carriedError: unknown) => {
              if (carriedError instanceof HttpErrorResponse) {
                const carriedErrMsg = this.getApiErrorMessage(carriedError);
                if (carriedErrMsg) {
                  this.toastr.error(carriedErrMsg);
                }
              }
              return throwError(() => carriedError);
            })
          );
        })
      );
    } else {
      return this.waitForTokenRefresh().pipe(
        catchError(err => {
          // If the primary refresh failed, all waiting requests get the error here
          return throwError(() => err);
        }),
        switchMap((newTokenResponse: LoginApiResponse) => {
          return next.handle(this.addNewToken(request, newTokenResponse)).pipe(
            catchError((carriedError: unknown) => {
              if (carriedError instanceof HttpErrorResponse) {
                const carriedErrMsg = this.getApiErrorMessage(carriedError);
                if (carriedErrMsg) {
                  this.toastr.error(carriedErrMsg);
                }
              }
              return throwError(() => carriedError);
            })
          );
        })
      );
    }
  }

  private addNewToken(
    request: HttpRequest<unknown>,
    newTokenResponse: LoginApiResponse
  ): HttpRequest<unknown> {
    const tokenResponseArray = newTokenResponse.data;

    if (tokenResponseArray && tokenResponseArray.length > 0) {
      const tokenResponse: TokenResponse | undefined = tokenResponseArray[0];

      if (tokenResponse && tokenResponse.accessToken) {
        localStorage.setItem('token', tokenResponse.accessToken);
        if (tokenResponse.refreshToken) {
          localStorage.setItem('refreshToken', tokenResponse.refreshToken);
        }
        return this.addToken(
          request,
          tokenResponse.accessToken,
          localStorage.getItem('refreshToken'),
          false
        );
      }
    }

    console.error('No valid access token found in the refresh token response');
    this.redirectService.setRedirectUrl(this.router.url);
    this.localStorageService.clear();

    this.router.navigate(['/auth']);
    throw new Error(
      'No valid access token found in the refresh token response'
    );
  }

  private waitForTokenRefresh(): Observable<LoginApiResponse> {
    return this.refreshTokenSubject.asObservable().pipe(
      filter((token): token is LoginApiResponse => token !== null),
      take(1)
    );
  }

  private getApiErrorMessage(error: HttpErrorResponse): string {
    if (!error) {
      return 'An unknown error occurred.';
    }
    const body = error.error;
    if (!body) {
      return error.message || 'An unknown error occurred.';
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return parsed.message || parsed.error || body;
      } catch (e) {
        return body;
      }
    }
    if (typeof body === 'object') {
      if (body.message) {
        return body.message;
      }
      if (body.error) {
        if (typeof body.error === 'object' && body.error.message) {
          return body.error.message;
        }
        if (typeof body.error === 'string') {
          return body.error;
        }
      }
      if (Array.isArray(body.errors) && body.errors.length > 0) {
        return body.errors[0];
      }
    }
    return error.message || 'An unknown error occurred.';
  }
}
